import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

import Pagination from "../shared/Tables/Pagination";
import { IContact } from "../../models/Contact";

const colHelper = createColumnHelper<IContact>();

export default function AccountTable({
  data,
}: {
  data: IContact[];
}) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);

  const cols = [
    colHelper.accessor("account.accountNumber", {
      header: "Account Number",
      cell: info => info.getValue() || "N/A"
    }),
    colHelper.accessor("fullname", {
      header: "Contact Name",
      cell: info => info.getValue() || "N/A"
    }),
    colHelper.accessor("email", {
      header: "Primary Email",
      cell: info => info.getValue() || "N/A"
    }),
    colHelper.accessor("telephone1", {
      header: "Primary Telephone",
      cell: info => info.getValue() || "N/A"
    }),
    colHelper.accessor("account.name", {
      header: "Account Name",
      cell: info => info.getValue() || "N/A"
    }),
  ];

  const table = useReactTable({
    data: data,
    columns: cols,
    state: {
      sorting: sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    table.setPageSize(50);
  }, []);

  return (
    <div className="mt-4 bg-white">
      <table className="min-w-full divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: " ▲",
                        desc: " ▼",
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => row.original.account?.id && navigate(`${row.original.account.id}`)}
              className="hover:bg-gray-100 hover:cursor-pointer"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-3 border text-sm">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination table={table} />
    </div>
  );
}
